import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import TuneIcon from '@mui/icons-material/Tune';
import LockIcon from '@mui/icons-material/Lock';

function ModeSelector({ onModeSelect }) {
  const [selectedMode, setSelectedMode] = useState(null);

  const modes = [
    {
      id: 'STANDARD',
      title: 'Standard Mode',
      subtitle: 'Recommended for Students & Quick Planning',
      icon: <SpeedIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
      color: 'primary',
      features: [
        'Pre-configured NATO standard defaults',
        'Auto-allocated medical assets based on force size',
        'Standard MTF distances and evacuation chains',
        'Default casualty rates from Dupuy methodology',
        'Minimal user input required',
        'Fast scenario creation (5-10 minutes)',
        'Ideal for educational exercises'
      ],
      bestFor: 'Students, quick planning, standard NATO scenarios'
    },
    {
      id: 'EXPERT',
      title: 'Expert Mode',
      subtitle: 'Full Customization for Advanced Planning',
      icon: <TuneIcon sx={{ fontSize: 60, color: 'secondary.main' }} />,
      color: 'secondary',
      features: [
        'Full customization of all parameters',
        'Manual medical asset allocation',
        'Custom MTF distances and capabilities',
        'Adjustable casualty rate modifiers',
        'Detailed environmental factors',
        'Advanced force structure options',
        'Complete control over calculations'
      ],
      bestFor: 'Experienced planners, detailed analysis, custom scenarios'
    }
  ];

  const handleSelect = (modeId) => {
    setSelectedMode(modeId);
  };

  const handleConfirm = () => {
    if (selectedMode) {
      onModeSelect(selectedMode);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={10}
          sx={{
            p: 4,
            borderRadius: 2,
            background: 'white'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SettingsIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              Select Operating Mode
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Choose how you want to use the PSP/CRE Tool. This setting will be locked 
              for this scenario and cannot be changed later.
            </Typography>
          </Box>

          {/* Warning about mode locking */}
          <Alert severity="info" icon={<LockIcon />} sx={{ mb: 4 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Once selected, the mode cannot be changed for this scenario. 
              You can create new scenarios with different modes at any time.
            </Typography>
          </Alert>

          {/* Mode Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {modes.map((mode) => (
              <Grid size={{ xs: 12, md: 6 }} key={mode.id}>
                <Card
                  raised={selectedMode === mode.id}
                  sx={{
                    height: '100%',
                    border: selectedMode === mode.id ? 3 : 1,
                    borderColor: selectedMode === mode.id ? `${mode.color}.main` : 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                  onClick={() => handleSelect(mode.id)}
                >
                  <CardContent sx={{ pb: 1 }}>
                    {/* Icon and Title */}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      {mode.icon}
                      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
                        {mode.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {mode.subtitle}
                      </Typography>
                      {selectedMode === mode.id && (
                        <Chip
                          icon={<CheckCircleOutlineIcon />}
                          label="Selected"
                          color={mode.color}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>

                    {/* Features List */}
                    <List dense>
                      {mode.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleOutlineIcon color={mode.color} fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {/* Best For */}
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: `${mode.color}.50`, borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Best For:
                      </Typography>
                      <Typography variant="body2">
                        {mode.bestFor}
                      </Typography>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button
                      variant={selectedMode === mode.id ? 'contained' : 'outlined'}
                      color={mode.color}
                      size="large"
                      onClick={() => handleSelect(mode.id)}
                      sx={{ minWidth: 150 }}
                    >
                      {selectedMode === mode.id ? 'Selected' : 'Select'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Confirmation Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              disabled={!selectedMode}
              onClick={handleConfirm}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 3
              }}
            >
              {selectedMode 
                ? `Continue with ${modes.find(m => m.id === selectedMode)?.title} →`
                : 'Select a Mode to Continue'
              }
            </Button>

            {selectedMode && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 2 }}
              >
                You selected {modes.find(m => m.id === selectedMode)?.title}. 
                This cannot be changed after proceeding.
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default ModeSelector;
